import React, { useEffect, useState } from "react";
import { Users, UserPlus, Plus, Search, XIcon } from "lucide-react";
import { CardTitle } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Badge } from "@/modern/components/ui/badge";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { EntityContactProvider, useEntityContactContext } from "./components/context/EntityContactContext";
import { AssignContactEditor } from "@/react/components/entityViews/contacts/components/editors/AssignContactEditor";
import { ContactCard } from "@/react/components/entityViews/contacts/components/ContactCard";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/modern/components/ui/tooltip";
import { useEntityManagerContext } from "@/react/components/entityManager/context/EntityManagerContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";

// Empty state component
const EmptyContactsState = ({ onAssignContact, canEdit }: { onAssignContact: () => void; canEdit: boolean }) => {
  return (
    <div className="flex flex-1 items-center justify-center h-full p-4">
      <div className="h-full w-full">
        <div className="min-h-[12rem] h-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Users className="size-5" />
          </span>
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-foreground">No contacts yet</h3>
            <p className="text-xs text-muted-foreground">Add contacts to track important people related to this record.</p>
          </div>
          <Button onClick={onAssignContact} disabled={!canEdit}>
            <Plus className="h-4 w-4 mr-2" />
            Assign Contact
          </Button>
        </div>
      </div>
    </div>
  );
};

// Header component
const Header: React.FC<{ onCreateContact: () => void; onAssignContact: () => void }> = ({ onCreateContact, onAssignContact }) => {
  const { canEdit, searchTerm, setSearchTerm, isSearchOpen, setIsSearchOpen, contacts } = useEntityContactContext();

  // const menuItems: MenuItem[] = [
  //   {
  //     itemVariant: "button",
  //     menuText: "New Contact",
  //     Component: ({ onFinish }) => (
  //       <div
  //         className="flex items-center w-full cursor-pointer"
  //         onClick={(e) => {
  //           e.stopPropagation();
  //           onCreateContact();
  //           onFinish?.();
  //         }}
  //       >
  //         <Plus className="h-4 w-4 mr-2" />
  //         <span>New Contact</span>
  //       </div>
  //     )
  //   },
  //   {
  //     itemVariant: "button",
  //     menuText: "Assign Contact",
  //     Component: ({ onFinish }) => (
  //       <div
  //         className="flex items-center w-full cursor-pointer"
  //         onClick={(e) => {
  //           e.stopPropagation();
  //           onAssignContact();
  //           onFinish?.();
  //         }}
  //       >
  //         <UserPlus className="h-4 w-4 mr-2" />
  //         <span>Assign Contact</span>
  //       </div>
  //     )
  //   }
  // ];

  return (
    <div className="flex items-center justify-between w-full">
      <CardTitle className="text-base relative">
        Contacts
        {contacts.length > 0 && (
          <Badge variant="muted" size="xs" className="absolute -right-7 -top-2">
            {contacts.length}
          </Badge>
        )}
      </CardTitle>
      <ButtonGroup>
        {isSearchOpen && (
          <>
            <Input
              inputSize="xs"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              // variant={"ghost"}
              className="flex-1 h-auto bg-background dark:bg-background focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0  shadow-none px-4 "
              autoFocus
            />
            <Button
              variant="entityHeader"
              size="icon"
              // className="bg-transparent border-none hover:bg-transparent size-6 p-0"
              onClick={() => {
                setSearchTerm("");
                setIsSearchOpen(false);
              }}
            >
              <XIcon />
            </Button>
          </>
        )}
        {/* // left here in case we want to bring the search back .. flip the boolean */}
        {isSearchOpen && (
          <Button
            variant="entityHeader"
            size="icon"
            disabled={!canEdit}
            //  className="bg-transparent border-none hover:bg-transparent size-6 p-0"
            onClick={() => setIsSearchOpen(true)}
          >
            <Search />
          </Button>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="entityHeader"
              size="icon"
              disabled={!canEdit}
              onClick={(e) => {
                e.stopPropagation();
                onAssignContact();
                // onFinish?.();
              }}
            >
              <UserPlus />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Assign contact</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="entityHeader"
              size="icon"
              disabled={!canEdit}
              onClick={(e) => {
                e.stopPropagation();
                onCreateContact();
                // onFinish?.();
              }}
            >
              <Plus />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>New contact</p>
          </TooltipContent>
        </Tooltip>
        {/* <MoreMenu menuItems={menuItems} disabled={!canEdit} triggerButtonVariant="entityHeader" triggerButtonSize="icon" /> */}
      </ButtonGroup>
    </div>
  );
};

// Main component content
const EntityContactViewContent: React.FC = () => {
  const { contacts, searchTerm, filteredContacts, refreshContacts, canEdit, entityRecord, createPhantomContact } = useEntityContactContext();
  const { editorHandler } = useSwivuBryntumContext();

  // Dialog state
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const { version: contactsVersion } = useSwivuStore({ storeId: STORE_KEYS.contactStore });
  const { version: taskContactsVersion } = useSwivuStore({ storeId: STORE_KEYS.taskContactStore });
  const { version: resourceContactsVersion } = useSwivuStore({ storeId: STORE_KEYS.resourceContactStore });
  const { version: clientContactsVersion } = useSwivuStore({ storeId: STORE_KEYS.clientContactStore });
  const { version: userContactsVersion } = useSwivuStore({ storeId: STORE_KEYS.userContactStore });

  // Refresh contacts when stores change
  useEffect(() => {
    refreshContacts();
  }, [contactsVersion, taskContactsVersion, resourceContactsVersion, clientContactsVersion, userContactsVersion, refreshContacts]);

  const handleCreateContact = () => {
    const phantomContact = createPhantomContact();
    editorHandler.openEditor(
      EDITOR_KEYS.contactEditor,
      { id: phantomContact.id },
      {
        contact: phantomContact,
        entityRecord
      },
      { by: "recordType", useExisting: true }
    );
  };

  return (
    <>
      <DashboardCard headerClassName="" cardContentClassName="!p-0" header={<Header onCreateContact={handleCreateContact} onAssignContact={() => setIsAssignOpen(true)} />}>
        <div className="h-full w-full flex flex-col">
          {contacts.length === 0 ? (
            <EmptyContactsState onAssignContact={() => setIsAssignOpen(true)} canEdit={canEdit} />
          ) : filteredContacts.length === 0 ? (
            <div className="flex items-center justify-center h-full py-12 px-4">
              <div className="text-center text-muted-foreground">
                <p className="text-sm">No contacts found matching "{searchTerm}"</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1">
              {filteredContacts.map((contact) => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          )}
        </div>
      </DashboardCard>

      {/* Assign Contacts Dialog */}
      <AssignContactEditor
        open={isAssignOpen}
        onOpenChange={setIsAssignOpen}
        onCreateContact={handleCreateContact}
      />
    </>
  );
};

export const EntityContactView = () => {
  const { record: entityRecord } = useEntityManagerContext();

  return (
    <EntityContactProvider entityRecord={entityRecord}>
      <EntityContactViewContent />
    </EntityContactProvider>
  );
};
